import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { AppLoggingService } from '@core/services/app-logging.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { environment } from '@env/environment';
import { AppErrorService } from '@core/services/app-error.service';
import { AuthService } from '../../auth/auth.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { BCM_HTTP_OPTIONAL_READ, BCM_HTTP_SUPPRESS_ERRORS } from '@shared/constants/bcm-http-headers';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {

    private static readonly I18N_ERROR_KEY_PATTERN = /^(bcmErrors|common|accounting|bcm|nav|widgets|boat|boats|person)\./;

    private _errorService = new AppErrorService();

    constructor(
        private appLoggingService: AppLoggingService,
        private appNotificationService: AppNotificationService,
        private authService: AuthService,
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        const suppressErrors = request.headers.get(BCM_HTTP_SUPPRESS_ERRORS) === 'true';
        const optionalRead = request.headers.get(BCM_HTTP_OPTIONAL_READ) === 'true';

        return next
            .handle(request)
            .pipe(
                debounceTime(100), // front-end errors are often triggered very often
                catchError((error: HttpErrorResponse) => {

                    if (suppressErrors) {
                        return throwError(() => error);
                    }

                    let errorMessage = TolgeeService.translate('common.error.unexpectedWithSupportHint');
                    let skipNotification = false;

                    if (error.status === 401) {
                        // Unauthorized
                        errorMessage = TolgeeService.translate('common.error.notAuthenticatedRedirect');

                        setTimeout(async () => {
                            this.authService.signOut();
                        }, 3000);
                    } else if (error.error instanceof ErrorEvent) {
                        // client-side error
                        const message = this._errorService.getClientMessage(error as HttpErrorResponse);
                        const stack = this._errorService.getClientStack(error);

                        if (environment.production) {
                            this.appLoggingService.logError(message, error, stack);
                            skipNotification = true;
                        } else {
                            errorMessage = `[Fehler ${error.status}]: ${error.error.message}`;
                        }
                    } else {
                        // server-side error
                        if (error.error?.name === 'BcmError' || error.error?.name === 'Forbidden') {
                            errorMessage = this._resolveErrorMessage(
                                error.error.message,
                                error.error.translationParams,
                            );
                            // Optional background reads stay silent on 403, whatever the error body (#7296).
                            if (error.status === 403 && optionalRead) {
                                skipNotification = true;
                            }
                        } else if (error.status === 403) {
                            errorMessage = TolgeeService.translate('common.error.forbidden');
                            if (suppressErrors || optionalRead) {
                                skipNotification = true;
                            }
                        } else {
                            if (environment.production) {
                                if (error.status < 500) {
                                    skipNotification = true;
                                }
                            } else {
                                errorMessage = `Fehler-Status: ${error.status}<br>Meldung: HTTP-Anfrage fehlgeschlagen (${error.url || 'unbekannte URL'})<br>Details: ${error.error?.message || 'keine weiteren Details verfügbar'}`;
                            }
                        }
                    }

                    if (error.error instanceof Blob) {
                        error.error.text().then(err => {
                            let blobError: any;
                            try {
                                blobError = JSON.parse(err);
                            } catch {
                                this.appNotificationService.showError(errorMessage);
                                return;
                            }
                            if (blobError.name === 'BcmError' || blobError.name === 'Forbidden') {
                                errorMessage = this._resolveErrorMessage(
                                    blobError.message,
                                    blobError.translationParams,
                                );
                            } else {
                                if (environment.production) {
                                    if (blobError.status < 500) {
                                        skipNotification = true;
                                    }
                                } else {
                                    errorMessage = `Fehler-Status: ${blobError.status}<br>Meldung: HTTP-Anfrage fehlgeschlagen<br>Details: ${blobError.message || 'keine weiteren Details verfügbar'}`;
                                }
                            }
                            this.appNotificationService.showError(errorMessage);
                        });
                    } else if (errorMessage && !skipNotification) {
                        this.appNotificationService.showError(errorMessage);
                    }

                    return throwError(() => error);
                })
            );
    }

    private _resolveErrorMessage(
        message: string | undefined,
        translationParams?: Record<string, unknown>,
    ): string {
        if (!message) {
            return TolgeeService.translate('common.error.unexpectedWithSupportHint');
        }

        if (HttpErrorInterceptor.I18N_ERROR_KEY_PATTERN.test(message)) {
            return TolgeeService.translate(message, translationParams as Record<string, string>);
        }

        return message;
    }
}
